import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { api, authHeaders, formatApiErrorDetail } from '@/lib/api';
import AdminContentPanel from '@/components/AdminContentPanel';
import AdminDashboard from '@/components/AdminDashboard';

const COLLECTIONS = {
  hero: {
    label: 'Hero Campaigns', keyField: 'artist_name',
    fields: [
      ['kicker', 'Kicker (e.g. BRAYO RECORDS PRESENTS)', 'text'],
      ['artist_name', 'Artist Name', 'text'],
      ['release_title', 'Release Title', 'text'],
      ['label', 'Badge (e.g. OUT NOW)', 'text'],
      ['tagline', 'Tagline', 'text'],
      ['image_url', 'Campaign Image', 'image'],
      ['listen_url', 'Listen URL (opens when the hero is clicked)', 'text'],
      ['active', 'Active (show in slider)', 'checkbox'],
    ],
  },
  artists: {
    label: 'Artists', keyField: 'name',
    fields: [
      ['name', 'Artist Name', 'text'],
      ['genre', 'Genre', 'text'],
      ['image_url', 'Portrait Image', 'image'],
      ['bio', 'Bio', 'textarea'],
      ['slug', 'Slug (auto if empty)', 'text'],
      ['instagram', 'Instagram URL', 'text'],
      ['spotify', 'Spotify URL', 'text'],
      ['apple_url', 'Apple Music URL', 'text'],
      ['sort_order', 'Sort Order', 'number'],
      ['featured', 'Featured — first two featured artists (by Sort Order) get the big roster circles', 'checkbox'],
    ],
  },
  videos: {
    label: 'Videos', keyField: 'title',
    fields: [
      ['title', 'Video Title', 'text'],
      ['artist', 'Artist', 'text'],
      ['youtube_id', 'YouTube ID or full video URL', 'text'],
      ['vimeo_id', 'Vimeo ID or full video URL (optional)', 'text'],
      ['thumbnail_url', 'Thumbnail', 'image'],
      ['date', 'Date (YYYY-MM-DD)', 'text'],
      ['credit', 'Credit', 'text'],
    ],
  },
};

const FIELD_CLS = 'w-full border border-brayo-line bg-white px-3 py-2.5 text-base md:text-sm text-brayo-charcoal placeholder:text-brayo-dim focus:border-brayo-blue focus:outline-none';

export function extractYoutubeId(v = '') {
  const s = String(v).trim();
  if (!s) return '';
  const m = s.match(/(?:v=|youtu\.be\/|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : s;
}

export function extractVimeoId(v = '') {
  const s = String(v).trim();
  if (!s) return '';
  const m = s.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : s.replace(/\D/g, '');
}

function ImageField({ label, value, onChange, testid, token }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/admin/upload', fd, {
        headers: { ...authHeaders(token), 'Content-Type': 'multipart/form-data' },
      });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };
  return (
    <div>
      <span className="meta-label mb-2 block text-brayo-dim">{label}</span>
      <div className="flex gap-2">
        <input data-testid={testid} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder="https://… or upload a file" className={FIELD_CLS} />
        <label className="meta-label flex shrink-0 cursor-pointer items-center border border-brayo-blue px-3 text-brayo-blue transition-colors hover:bg-brayo-blue hover:text-white">
          {busy ? 'Uploading…' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`${testid}-file`} />
        </label>
      </div>
      {value && <img src={value} alt="preview" className="mt-2 h-20 w-20 border border-brayo-line object-cover" />}
    </div>
  );
}

function CollectionManager({ token, collection, config }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get(`/${collection}`).then(({ data }) => setItems(Array.isArray(data) ? data : data ? [data] : [])).catch(() => {});
  };
  useEffect(load, [collection, token]);

  const save = async (e) => {
    e.preventDefault();
    const body = {};
    config.fields.forEach(([key, , type]) => {
      const v = editing[key];
      body[key] = type === 'number' ? Number(v) || 0 : v;
    });
    if ('youtube_id' in body) body.youtube_id = extractYoutubeId(body.youtube_id);
    if ('vimeo_id' in body) body.vimeo_id = extractVimeoId(body.vimeo_id);
    try {
      if (editing.id) await api.put(`/admin/${collection}/${editing.id}`, body, { headers: authHeaders(token) });
      else await api.post(`/admin/${collection}`, body, { headers: authHeaders(token) });
      toast.success(`${config.label} saved`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/admin/${collection}/${id}`, { headers: authHeaders(token) });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const blank = () => {
    const obj = {};
    config.fields.forEach(([key, , type]) => { obj[key] = type === 'checkbox' ? false : ''; });
    return obj;
  };

  return (
    <div data-testid={`admin-collection-${collection}`}>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="display-title text-2xl md:text-3xl">{config.label}</h2>
        <button
          data-testid={`admin-new-${collection}-btn`}
          onClick={() => setEditing(blank())}
          className="meta-label bg-brayo-blue px-4 py-2.5 text-white transition-colors hover:bg-brayo-charcoal"
        >
          + New
        </button>
      </div>

      <ul className="divide-y divide-brayo-line border-y border-brayo-line">
        {items.map((item) => (
          <li key={item.id} data-testid={`admin-item-${collection}-${item.id}`} className="flex items-center justify-between gap-4 py-3">
            <span className="truncate text-sm text-brayo-charcoal/85">
              {item[config.keyField] || '(untitled)'}
              {collection === 'hero' && <span className={`meta-label ml-3 ${item.active ? 'text-brayo-blue' : 'text-brayo-dim'}`}>{item.active ? 'LIVE' : 'inactive'}</span>}
            </span>
            <span className="flex shrink-0 gap-2">
              <button data-testid={`admin-edit-${collection}-${item.id}`} onClick={() => setEditing({ ...item })} className="meta-label border border-brayo-line px-3 py-1.5 text-brayo-charcoal hover:border-brayo-blue hover:text-brayo-blue">Edit</button>
              <button data-testid={`admin-delete-${collection}-${item.id}`} onClick={() => del(item.id)} className="meta-label border border-brayo-line px-3 py-1.5 text-brayo-charcoal hover:border-red-500 hover:text-red-500">Delete</button>
            </span>
          </li>
        ))}
        {items.length === 0 && <li className="py-4 text-sm text-brayo-dim">Nothing here yet.</li>}
      </ul>

      <AnimatePresence>
        {editing && (
          <motion.div className="fixed inset-0 z-50 overflow-y-auto bg-brayo-charcoal/40 p-5 backdrop-blur-sm md:p-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setEditing(null)}>
            <motion.form
              onSubmit={save}
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
              className="mx-auto max-w-2xl border border-brayo-line bg-brayo-paper p-6 md:p-8"
              data-testid={`admin-form-${collection}`}
            >
              <h3 className="display-title mb-6 text-xl">{editing.id ? 'Edit' : 'New'} — {config.label}</h3>
              <div className="space-y-4">
                {config.fields.map(([key, label, type]) => (
                  type === 'image' ? (
                    <ImageField
                      key={key}
                      label={label}
                      value={editing[key]}
                      onChange={(v) => setEditing({ ...editing, [key]: v })}
                      testid={`admin-field-${key}`}
                      token={token}
                    />
                  ) : (
                    <label key={key} className="block">
                      <span className="meta-label mb-2 block text-brayo-dim">{label}</span>
                      {type === 'textarea' ? (
                        <textarea data-testid={`admin-field-${key}`} rows={4} value={editing[key] ?? ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} className={`${FIELD_CLS} resize-none`} />
                      ) : type === 'checkbox' ? (
                        <input data-testid={`admin-field-${key}`} type="checkbox" checked={!!editing[key]} onChange={(e) => setEditing({ ...editing, [key]: e.target.checked })} className="h-5 w-5 accent-[#4FA8E8]" />
                      ) : (
                        <input data-testid={`admin-field-${key}`} type={type} value={editing[key] ?? ''} onChange={(e) => setEditing({ ...editing, [key]: e.target.value })} className={FIELD_CLS} />
                      )}
                    </label>
                  )
                ))}
              </div>
              <div className="mt-8 flex gap-3">
                <button data-testid={`admin-save-${collection}-btn`} type="submit" className="meta-label bg-brayo-blue px-6 py-3 text-white transition-colors hover:bg-brayo-charcoal">Save</button>
                <button type="button" onClick={() => setEditing(null)} className="meta-label border border-brayo-line px-6 py-3 text-brayo-charcoal hover:border-brayo-blue">Cancel</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SettingsPanel({ token }) {
  const [form, setForm] = useState(null);
  useEffect(() => {
    api.get('/settings').then(({ data }) => setForm(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put('/admin/settings', form, { headers: authHeaders(token) });
      toast.success('Settings saved');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!form) return <p className="meta-label text-brayo-dim">Loading…</p>;

  const socialFields = [
    ['instagram_url', 'Instagram URL'],
    ['x_url', 'X (Twitter) URL'],
    ['youtube_url', 'YouTube URL'],
    ['spotify_url', 'Spotify URL'],
    ['tiktok_url', 'TikTok URL'],
    ['shop_url', 'Shop Website URL (the SHOP menu and every Buy button open it)'],
  ];

  return (
    <div data-testid="admin-settings">
      <h2 className="display-title mb-2 text-2xl md:text-3xl">Social & Contact</h2>
      <p className="mb-8 text-sm text-brayo-mute">These links power the social icon buttons in the site footer.</p>
      <form onSubmit={submit} className="max-w-xl space-y-5" data-testid="admin-settings-form">
        {socialFields.map(([key, label]) => (
          <label key={key} className="block">
            <span className="meta-label mb-2 block text-brayo-dim">{label}</span>
            <input data-testid={`admin-settings-${key}`} value={form[key] ?? ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={FIELD_CLS} />
          </label>
        ))}
        <button data-testid="admin-settings-save-btn" type="submit" className="meta-label bg-brayo-blue px-6 py-3 text-white transition-colors hover:bg-brayo-charcoal">Save Settings</button>
      </form>
    </div>
  );
}

function SubmissionsPanel({ token }) {
  const [items, setItems] = useState([]);
  const load = () => { api.get('/admin/submissions', { headers: authHeaders(token) }).then(({ data }) => setItems(data)).catch(() => {}); };
  useEffect(load, [token]);

  return (
    <div data-testid="admin-submissions">
      <h2 className="display-title mb-6 text-2xl md:text-3xl">Submissions</h2>
      <ul className="space-y-4">
        {items.map((s) => (
          <li key={s.id} data-testid={`admin-submission-${s.id}`} className="border border-brayo-line bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-bold text-brayo-charcoal">{s.name} <span className="meta-label text-brayo-dim">— {s.department}</span>
                {s.unsubscribed && <span data-testid={`admin-submission-unsubscribed-${s.id}`} className="meta-label ml-2 border border-brayo-line px-2 py-0.5 text-brayo-dim">Unsubscribed</span>}
              </p>
              <button data-testid={`admin-submission-delete-${s.id}`} onClick={() => { if (window.confirm('Delete?')) api.delete(`/admin/submissions/${s.id}`, { headers: authHeaders(token) }).then(load); }} className="meta-label text-brayo-dim hover:text-red-500">Delete</button>
            </div>
            <p className="meta-label mt-1 text-brayo-blue">{s.email}</p>
            {(s.artist_name || s.genre) && <p className="mt-2 text-sm text-brayo-charcoal/80">{s.artist_name} {s.genre && `· ${s.genre}`}</p>}
            {s.message && <p className="mt-2 text-sm leading-relaxed text-brayo-mute">{s.message}</p>}
            {s.link && <a href={s.link} target="_blank" rel="noopener noreferrer" className="meta-label mt-2 block text-brayo-blue hover:text-brayo-charcoal">{s.link}</a>}
            <p className="meta-label mt-2 text-brayo-dim">{s.created_at?.slice(0, 10)}</p>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-brayo-dim">No submissions yet.</li>}
      </ul>
    </div>
  );
}

function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-brayo-paper px-5">
      <form onSubmit={submit} className="w-full max-w-sm border border-brayo-line bg-white p-8" data-testid="admin-login-form">
        <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className="mx-auto mb-8 h-12" />
        <h1 className="display-title mb-8 text-center text-2xl text-brayo-charcoal">Label Login</h1>
        {error && <p data-testid="admin-login-error" className="mb-4 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>}
        <label className="block">
          <span className="meta-label mb-2 block text-brayo-dim">Email</span>
          <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={FIELD_CLS} />
        </label>
        <label className="mt-4 block">
          <span className="meta-label mb-2 block text-brayo-dim">Password</span>
          <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={FIELD_CLS} />
        </label>
        <button data-testid="admin-login-submit" type="submit" disabled={busy} className="meta-label mt-6 w-full bg-brayo-blue py-3.5 text-white transition-colors hover:bg-brayo-charcoal disabled:opacity-50">
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const { token, user, checking, logout } = useAuth();
  const [tab, setTab] = useState('dashboard');

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-brayo-paper"><p className="meta-label text-brayo-dim">Checking session…</p></div>;
  }
  if (!token || !user) return <AdminLogin />;

  const tabs = [
    ['dashboard', 'Dashboard'],
    ...Object.entries(COLLECTIONS).map(([k, c]) => [k, c.label]),
    ['content', 'Site Content'],
    ['settings', 'Social & Contact'],
    ['submissions', 'Submissions'],
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brayo-paper">
      <header className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-4 border-b border-brayo-line bg-brayo-paper/95 px-5 py-4 backdrop-blur md:px-10">
        <div className="flex items-center gap-4">
          <img src="/brand/brayo-logo-alpha.png" alt="Brayo Records" className="h-8" />
          <span className="meta-label hidden text-brayo-dim sm:block">Label Manager</span>
        </div>
        <div className="flex items-center gap-4">
          <a data-testid="admin-view-site-link" href="/" className="meta-label border border-brayo-line px-4 py-2 text-brayo-charcoal transition-colors hover:border-brayo-blue hover:text-brayo-blue">View Site</a>
          <button data-testid="admin-logout-btn" onClick={logout} className="meta-label bg-brayo-blue px-4 py-2 text-white transition-colors hover:bg-brayo-charcoal">Log Out</button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1300px] gap-6 px-5 py-8 md:px-10">
        <nav className="hidden shrink-0 md:block">
          <ul className="sticky top-24 space-y-1">
            {tabs.map(([key, label]) => (
              <li key={key}>
                <button
                  data-testid={`admin-tab-${key}`}
                  onClick={() => setTab(key)}
                  className={`meta-label w-full px-4 py-2.5 text-left transition-colors ${tab === key ? 'bg-brayo-blue text-white' : 'text-brayo-charcoal/70 hover:text-brayo-blue'}`}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="no-scrollbar fixed inset-x-0 bottom-0 z-40 flex overflow-x-auto border-t border-brayo-line bg-brayo-paper md:hidden">
          {tabs.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-mobile-${key}`} onClick={() => setTab(key)} className={`shrink-0 px-3.5 py-4 text-[10px] font-bold uppercase tracking-widest ${tab === key ? 'text-brayo-blue' : 'text-brayo-charcoal/60'}`}>
              {label}
            </button>
          ))}
        </nav>

        <main className="w-full pb-24 md:pb-8">
          {tab === 'dashboard' && <AdminDashboard token={token} user={user} onNavigate={setTab} />}
          {tab === 'submissions' && <SubmissionsPanel token={token} />}
          {tab === 'content' && <AdminContentPanel token={token} />}
          {tab === 'settings' && <SettingsPanel token={token} />}
          {COLLECTIONS[tab] && <CollectionManager key={tab} token={token} collection={tab} config={COLLECTIONS[tab]} />}
        </main>
      </div>
    </div>
  );
}